import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { account } from '../appwrite/appwrite';

/**
 * Liest userId und secret aus dem Link.
 *
 * Appwrite haengt die Query-Parameter an die uebergebene URL an. Da die App
 * einen HashRouter nutzt, landen sie hinter dem Hash, z. B.
 *   https://chargenest.chargedesk.de/#/reset-password?userId=...&secret=...
 * Sicherheitshalber werden auch die klassischen Query-Parameter geprueft.
 */
const readRecoveryParams = () => {
  const hash = window.location.hash || '';
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const search = window.location.search || '';

  const fromHash = new URLSearchParams(hashQuery);
  const fromSearch = new URLSearchParams(search);

  const userId = fromHash.get('userId') || fromSearch.get('userId') || '';
  const secret = fromHash.get('secret') || fromSearch.get('secret') || '';

  return { userId, secret };
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const { userId, secret } = useMemo(() => readRecoveryParams(), []);
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const valid = Boolean(userId && secret);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError('');

    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (password !== passwordRepeat) {
      setError('Die beiden Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      await account.updateRecovery(userId, secret, password);
      setDone(true);
    } catch (err) {
      const message = String(err?.message || '');
      if (/token|expired|invalid/i.test(message)) {
        setError('Der Link ist abgelaufen oder wurde schon benutzt. Bitte fordere einen neuen an.');
      } else if (/password/i.test(message)) {
        setError('Das Passwort erfüllt die Anforderungen nicht (mindestens 8 Zeichen).');
      } else {
        setError('Zurücksetzen fehlgeschlagen. Bitte einen neuen Link anfordern.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-center">
            <div className="w-24 h-24 flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiIcons.FiKey} className="text-white" />
            </div>
            <p className="text-indigo-100 text-sm">ChargeNest</p>
            <h1 className="text-white text-xl font-bold mt-1">Neues Passwort setzen</h1>
          </div>

          <div className="p-8 space-y-5">
            {done ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} />
                  <span className="text-sm">Passwort gespeichert. Du kannst dich jetzt anmelden.</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  Zur Anmeldung
                </button>
              </div>
            ) : !valid ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiAlertCircle} />
                  <span className="text-sm">
                    Dieser Link ist unvollständig oder abgelaufen. Bitte fordere unter „Passwort
                    vergessen?" einen neuen an.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  Zur Anmeldung
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiAlertCircle} />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiIcons.FiLock} className="text-gray-400" />
                    </div>
                    <input
                      id="new-password"
                      name="new-password"
                      type="password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      placeholder="mindestens 8 Zeichen"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="repeat-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Passwort wiederholen
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiIcons.FiLock} className="text-gray-400" />
                    </div>
                    <input
                      id="repeat-password"
                      name="repeat-password"
                      type="password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      value={passwordRepeat}
                      onChange={(e) => setPasswordRepeat(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      placeholder="Passwort wiederholen"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? 'Wird gespeichert…' : 'Passwort speichern'}
                </button>
              </form>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
